/**
 * Prepare an image for CDN upload: as it is — never scaled down or re-compressed,
 * because factsheets are printed (agreed with SQUAD 29.10.2026). A file over the
 * upload limit is refused with a message in uploadImage instead.
 *
 * While production nginx had `client_max_body_size 1m`, everything above ~900 KB was
 * re-encoded here as JPEG on white, which flattened SQUAD's cut-out advisor photos
 * (28.10.2026). nginx allows 5m since 18.09.2026.
 *
 * Only HEIC/HEIF/TIFF are converted, because neither the browser preview nor the PDF
 * can show them — at full resolution, as PNG when transparent, otherwise JPEG.
 */

const CONVERT_TYPES = /heic|heif|tiff/i;

const JPEG_QUALITY = 0.92;

function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read image for conversion'));
    };
    img.src = url;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, type: 'image/jpeg' | 'image/png', quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Image conversion failed'));
          return;
        }
        resolve(blob);
      },
      type,
      quality,
    );
  });
}

/** Full resolution, nothing painted underneath. */
function drawFull(img: HTMLImageElement): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, img.naturalWidth);
  canvas.height = Math.max(1, img.naturalHeight);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas unavailable for image conversion');
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas;
}

/**
 * True when any pixel is not fully opaque — such an image must not become JPEG.
 * If the pixels cannot be read (canvas fingerprinting protection, memory), assume
 * transparency: a needless PNG costs bytes, a wrong JPEG costs the cut-out.
 */
function hasTransparentPixels(canvas: HTMLCanvasElement): boolean {
  try {
    const ctx = canvas.getContext('2d');
    if (!ctx) return true;
    const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] < 255) return true;
    }
    return false;
  } catch {
    return true;
  }
}

/** Return the file unchanged; only HEIC/HEIF/TIFF are converted, at full resolution. */
export async function prepareImageForUpload(file: File): Promise<File> {
  if (!CONVERT_TYPES.test(file.type)) return file;

  const img = await loadImageFromFile(file);
  const canvas = drawFull(img);
  const keepAlpha = hasTransparentPixels(canvas);
  // Opaque: the canvas already holds every pixel at full alpha, so no white fill is needed.
  const blob = keepAlpha
    ? await canvasToBlob(canvas, 'image/png')
    : await canvasToBlob(canvas, 'image/jpeg', JPEG_QUALITY);

  const baseName = file.name.replace(/\.[^.]+$/, '') || 'image';
  return new File([blob], `${baseName}.${keepAlpha ? 'png' : 'jpg'}`, {
    type: keepAlpha ? 'image/png' : 'image/jpeg',
    lastModified: Date.now(),
  });
}
